// Tax-friendly closed-position export. One row per position with everything
// an accountant or tax authority typically wants:
//   open/close timestamps, symbol, side, volume, prices, fees, P&L, attribution.
//
// Two output formats — XLSX (with header styling + totals row) and CSV
// (RFC 4180 escaped). Both pure data → blob; the dialog wires them up to
// download links.
//
// We dynamic-import the XLSX library inside the function so the ~600 kB
// SheetJS bundle never lands in the main JS payload — only loaded when
// the user actually clicks Export.

import type { ClosedPosition } from '@/types/veritas';

export type TaxRow = {
  position_ticket: number;
  account_login: number;
  opened_at: string;        // ISO
  closed_at: string;        // ISO
  symbol: string;
  side: 'BUY' | 'SELL' | '';
  volume: number;
  entry_price: number | null;
  exit_price: number | null;
  realized_profit: number;
  swap: number;
  commission: number;
  fee: number;
  net_pnl: number;          // realized + swap + commission + fee
  source: 'bot' | 'manual';
  magic: number;
  comments: string;
};

const HEADER_ORDER: (keyof TaxRow)[] = [
  'position_ticket', 'account_login', 'opened_at', 'closed_at', 'symbol',
  'side', 'volume', 'entry_price', 'exit_price', 'realized_profit',
  'swap', 'commission', 'fee', 'net_pnl', 'source', 'magic', 'comments',
];

const HEADER_LABELS: Record<keyof TaxRow, string> = {
  position_ticket: 'Position Ticket',
  account_login:   'Account',
  opened_at:       'Opened (UTC)',
  closed_at:       'Closed (UTC)',
  symbol:          'Symbol',
  side:            'Side',
  volume:          'Volume (lots)',
  entry_price:     'Entry',
  exit_price:      'Exit',
  realized_profit: 'Realized P&L',
  swap:            'Swap',
  commission:      'Commission',
  fee:             'Fee',
  net_pnl:         'Net P&L',
  source:          'Source',
  magic:           'Magic',
  comments:        'Comments',
};

// ── Filter + project to tax rows ─────────────────────────────────────────
export function buildTaxRows(
  positions: ClosedPosition[],
  range: { startDate: string; endDate: string },  // 'YYYY-MM-DD' inclusive
): TaxRow[] {
  const startMs = Date.parse(`${range.startDate}T00:00:00Z`);
  const endMs   = Date.parse(`${range.endDate}T23:59:59.999Z`);

  const out: TaxRow[] = [];
  for (const p of positions) {
    if (!p.closed_at) continue;
    const t = Date.parse(p.closed_at);
    if (!Number.isFinite(t)) continue;
    if (t < startMs || t > endMs) continue;

    const side: TaxRow['side'] =
      p.side_code === 0 ? 'BUY' : p.side_code === 1 ? 'SELL' : '';
    const realized   = p.realized_profit ?? 0;
    const swap       = p.total_swap ?? 0;
    const commission = p.total_commission ?? 0;
    const fee        = p.total_fee ?? 0;

    out.push({
      position_ticket: p.position_ticket,
      account_login:   p.account_login,
      opened_at:       p.opened_at ?? '',
      closed_at:       p.closed_at,
      symbol:          p.symbol,
      side,
      volume:          p.opened_volume ?? 0,
      entry_price:     p.entry_price,
      exit_price:      p.exit_price,
      realized_profit: realized,
      swap,
      commission,
      fee,
      net_pnl:         realized + swap + commission + fee,
      source:          p.magic && p.magic !== 0 ? 'bot' : 'manual',
      magic:           p.magic ?? 0,
      comments:        p.comments ?? '',
    });
  }
  // Chronological — accountants like time-ordered ledgers.
  out.sort((a, b) => a.closed_at.localeCompare(b.closed_at));
  return out;
}

// ── CSV ──────────────────────────────────────────────────────────────────
// Hand-rolled (no lib). RFC 4180 escaping: wrap any field that contains
// quote, comma, newline, or carriage return in double quotes; escape
// internal quotes by doubling.
function csvEscape(v: unknown): string {
  if (v == null) return '';
  const s = String(v);
  if (/[",\r\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

export function buildCsv(rows: TaxRow[]): string {
  const header = HEADER_ORDER.map((k) => csvEscape(HEADER_LABELS[k])).join(',');
  const lines = rows.map((r) =>
    HEADER_ORDER.map((k) => {
      const v = r[k];
      // Format numbers with up to 5 decimals (preserves price precision
      // for FX without losing currency cents).
      if (typeof v === 'number') {
        if (k === 'entry_price' || k === 'exit_price') return csvEscape(v.toFixed(5));
        if (k === 'volume') return csvEscape(v.toFixed(2));
        if (k === 'realized_profit' || k === 'swap' || k === 'commission'
            || k === 'fee' || k === 'net_pnl') return csvEscape(v.toFixed(2));
        return csvEscape(v);
      }
      return csvEscape(v);
    }).join(','),
  );
  // BOM so Excel detects UTF-8 — copes with accented symbols / comments.
  return '﻿' + [header, ...lines].join('\r\n') + '\r\n';
}

export function buildCsvBlob(rows: TaxRow[]): Blob {
  return new Blob([buildCsv(rows)], { type: 'text/csv;charset=utf-8' });
}

// ── XLSX ─────────────────────────────────────────────────────────────────
// Lazy-loads SheetJS so the main bundle stays lean. Sheet has:
//   Row 1: header (frozen, bold)
//   Row 2…N: data
//   Row N+1: TOTAL — sums numeric P&L columns
export async function buildXlsxBlob(
  rows: TaxRow[],
  meta: { accountLogin: number; rangeStart: string; rangeEnd: string },
): Promise<Blob> {
  const XLSX = await import('xlsx');

  // Build the sheet data as an AOA (array-of-arrays) so we control formatting.
  const headerRow = HEADER_ORDER.map((k) => HEADER_LABELS[k]);
  const dataRows = rows.map((r) => HEADER_ORDER.map((k) => {
    const v = r[k];
    // Convert ISO strings to JS Date so Excel treats them as datetimes.
    if ((k === 'opened_at' || k === 'closed_at') && typeof v === 'string' && v) {
      const d = new Date(v);
      return Number.isFinite(d.getTime()) ? d : v;
    }
    return v ?? '';
  }));

  // Totals row — only sum the money columns.
  const sum = (key: keyof TaxRow) => rows.reduce((a, r) => a + (r[key] as number || 0), 0);
  const totalsRow = HEADER_ORDER.map((k) => {
    if (k === 'realized_profit' || k === 'swap' || k === 'commission'
        || k === 'fee' || k === 'net_pnl') return sum(k);
    if (k === 'volume') return sum('volume');
    if (k === 'position_ticket') return 'TOTAL';
    return '';
  });

  const aoa: (string | number | Date)[][] = [headerRow, ...dataRows as never, totalsRow];
  // cellDates: true makes SheetJS recognise JS Date objects in the AOA
  // and serialise them as Excel serial-date numbers with the right
  // type. Without it, manual `cell.t = 'd'` gymnastics leave the file
  // claiming "type=date" while the value is a serial number — which
  // Excel rejects with the "we found a problem with some content"
  // dialog.
  const ws = XLSX.utils.aoa_to_sheet(aoa, { cellDates: true });

  // Column widths — eyeballed for "looks fine in Excel without resizing".
  ws['!cols'] = [
    { wch: 12 }, { wch: 10 }, { wch: 19 }, { wch: 19 }, { wch: 10 },
    { wch: 6 },  { wch: 10 }, { wch: 11 }, { wch: 11 }, { wch: 13 },
    { wch: 11 }, { wch: 11 }, { wch: 9 },  { wch: 13 }, { wch: 8 },
    { wch: 11 }, { wch: 30 },
  ];
  // Freeze top row. SheetJS exposes this via !views (NOT !freeze, which
  // is silently ignored and writes nothing — we used the wrong key
  // before).
  ws['!views'] = [{ state: 'frozen', xSplit: 0, ySplit: 1, topLeftCell: 'A2' }];

  // Apply display formats per column. We DO NOT touch cell.t — leave
  // type alone (SheetJS already inferred it from the JS value). We
  // only override the display format string `z`. Empty cells (null
  // opened_at on bot trades) are skipped so we don't tag a blank string
  // with a numeric format.
  const range = XLSX.utils.decode_range(ws['!ref'] as string);
  for (let R = range.s.r + 1; R <= range.e.r; R++) {
    for (let C = range.s.c; C <= range.e.c; C++) {
      const key = HEADER_ORDER[C];
      const cell = ws[XLSX.utils.encode_cell({ r: R, c: C })];
      if (!cell) continue;
      // Only apply numeric / date formatting to cells that are actually
      // numbers or dates — skip strings (incl. the empty string '') so
      // we don't paint a "0.00" format onto a missing cell.
      if (cell.t !== 'n' && cell.t !== 'd') continue;
      if (key === 'opened_at' || key === 'closed_at') {
        cell.z = 'yyyy-mm-dd hh:mm:ss';
      } else if (key === 'entry_price' || key === 'exit_price') {
        cell.z = '0.00000';
      } else if (key === 'volume') {
        cell.z = '0.00';
      } else if (key === 'realized_profit' || key === 'swap' || key === 'commission'
                 || key === 'fee' || key === 'net_pnl') {
        cell.z = '#,##0.00;[Red]-#,##0.00';
      }
    }
  }

  // Sheet name — Excel forbids special chars and >31 chars.
  const sheetName = `Trades ${meta.rangeStart}_${meta.rangeEnd}`
    .replace(/[\\\/:*?\[\]]/g, '-')
    .slice(0, 31);

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName);

  // Cover sheet with metadata so the export is self-describing.
  const meta_aoa: (string | number)[][] = [
    ['Veritas — Tax export'],
    [],
    ['Account', meta.accountLogin],
    ['Range start', meta.rangeStart],
    ['Range end', meta.rangeEnd],
    ['Position count', rows.length],
    ['Generated at (UTC)', new Date().toISOString()],
    [],
    ['Net P&L total', rows.reduce((a, r) => a + r.net_pnl, 0)],
    ['Realized P&L total', rows.reduce((a, r) => a + r.realized_profit, 0)],
    ['Swap total', rows.reduce((a, r) => a + r.swap, 0)],
    ['Commission total', rows.reduce((a, r) => a + r.commission, 0)],
    ['Fee total', rows.reduce((a, r) => a + r.fee, 0)],
  ];
  const metaWs = XLSX.utils.aoa_to_sheet(meta_aoa);
  metaWs['!cols'] = [{ wch: 22 }, { wch: 28 }];
  XLSX.utils.book_append_sheet(wb, metaWs, 'Summary');

  const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  return new Blob([buf], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
}

// ── Filename helpers ─────────────────────────────────────────────────────
export function taxFilename(
  accountLogin: number,
  rangeStart: string,
  rangeEnd: string,
  ext: 'csv' | 'xlsx',
): string {
  return `veritas_tax_${accountLogin}_${rangeStart}_to_${rangeEnd}.${ext}`;
}
